import {
  buildClinicianWorkbenchActionRequest,
  buildClinicianWorkbenchHttpQuery,
  clinicianWorkbenchOutboundCallsCommandContractVersion,
  clinicianWorkbenchOutboundCallsContractVersion,
  type ClinicianWorkbenchActionInput,
  type ClinicianWorkbenchActionRequestV1,
  type ClinicianWorkbenchActionResponseV1,
  type ClinicianWorkbenchDocumentCompleteUploadResponseV1,
  type ClinicianWorkbenchDocumentUploadIntentRequestV1,
  clinicianWorkbenchContractVersion,
  resolveClinicianWorkbenchDetailTab,
  type ClinicianWorkbenchHttpQuery,
  type ClinicianWorkbenchOutboundCallsCommandRequestV1,
  type ClinicianWorkbenchOutboundCallsCommandResponseV1,
  type ClinicianWorkbenchOutboundCallsRequestV1,
  type ClinicianWorkbenchOutboundCallsResponseV1,
  type ClinicianWorkbenchQuestionnaireDefinitionResponseV1,
  type ClinicianWorkbenchRequestV1,
  type ClinicianWorkbenchResponseV1,
} from "@amplecare/contracts";

import {
  resolveMobileRuntimeConfig,
  type MobileRuntimeConfig,
} from "../config/runtime-config.js";
import type { ClinicianWorkbenchRequestState } from "./types.js";

export type Awaitable<T> = T | Promise<T>;

export interface ClinicianWorkbenchTransportRequest {
  query: ClinicianWorkbenchHttpQuery;
  signal?: AbortSignal;
}

export interface ClinicianWorkbenchActionTransportRequest {
  request: ClinicianWorkbenchActionRequestV1;
  signal?: AbortSignal;
}

export interface ClinicianWorkbenchDocumentUploadTransportRequest {
  bytes: ArrayBuffer;
  request: Omit<
    ClinicianWorkbenchDocumentUploadIntentRequestV1,
    "schemaVersion"
  >;
  signal?: AbortSignal;
}

export interface ClinicianWorkbenchOutboundCallsTransportRequest {
  request: ClinicianWorkbenchOutboundCallsRequestV1;
  signal?: AbortSignal;
}

export interface ClinicianWorkbenchOutboundQuestionnaireAnswersPdfTransportRequest {
  patientId: string;
  sessionId?: string | null;
  signal?: AbortSignal;
  targetId: string;
}

export interface ClinicianWorkbenchOutboundQuestionnaireAnswersPdfTransportResponse {
  bodyBytes: ArrayBuffer;
  contentType: string;
  filename: string;
}

export interface ClinicianWorkbenchOutboundCallsCommandTransportRequest {
  request: ClinicianWorkbenchOutboundCallsCommandRequestV1;
  signal?: AbortSignal;
}

export interface ClinicianWorkbenchQuestionnaireDefinitionTransportRequest {
  request: {
    tenantId: string;
    siteId: string | null;
    clinicianUserId: string;
    /** The version the selected patient's session is pinned to, when it
     *  differs from the campaign's; omitted for the tenant default. */
    questionnaireTemplateVersionId?: string | null;
  };
  signal?: AbortSignal;
}

export interface ClinicianWorkbenchTransport {
  readClinicianWorkbench(
    input: ClinicianWorkbenchTransportRequest,
  ): Awaitable<ClinicianWorkbenchResponseV1>;
  submitClinicianWorkbenchAction(
    input: ClinicianWorkbenchActionTransportRequest,
  ): Awaitable<ClinicianWorkbenchActionResponseV1>;
  uploadClinicianWorkbenchDocument(
    input: ClinicianWorkbenchDocumentUploadTransportRequest,
  ): Awaitable<ClinicianWorkbenchDocumentCompleteUploadResponseV1>;
  readClinicianWorkbenchOutboundCalls(
    input: ClinicianWorkbenchOutboundCallsTransportRequest,
  ): Awaitable<ClinicianWorkbenchOutboundCallsResponseV1>;
  readClinicianWorkbenchQuestionnaireDefinition(
    input: ClinicianWorkbenchQuestionnaireDefinitionTransportRequest,
  ): Awaitable<ClinicianWorkbenchQuestionnaireDefinitionResponseV1>;
  downloadClinicianWorkbenchOutboundQuestionnaireAnswersPdf(
    input: ClinicianWorkbenchOutboundQuestionnaireAnswersPdfTransportRequest,
  ): Awaitable<ClinicianWorkbenchOutboundQuestionnaireAnswersPdfTransportResponse>;
  submitClinicianWorkbenchOutboundCallsCommand(
    input: ClinicianWorkbenchOutboundCallsCommandTransportRequest,
  ): Awaitable<ClinicianWorkbenchOutboundCallsCommandResponseV1>;
}

export interface ClinicianWorkbenchClientResult {
  request: ClinicianWorkbenchRequestV1;
  query: ClinicianWorkbenchHttpQuery;
  response: ClinicianWorkbenchResponseV1;
}

export interface ClinicianWorkbenchActionClientResult {
  request: ClinicianWorkbenchActionRequestV1;
  response: ClinicianWorkbenchActionResponseV1;
}

export interface ClinicianWorkbenchOutboundCallsClientResult {
  request: ClinicianWorkbenchOutboundCallsRequestV1;
  response: ClinicianWorkbenchOutboundCallsResponseV1;
}

export interface ClinicianWorkbenchOutboundCallsCommandClientResult {
  request: ClinicianWorkbenchOutboundCallsCommandRequestV1;
  response: ClinicianWorkbenchOutboundCallsCommandResponseV1;
}

export function buildClinicianWorkbenchShellRequest(
  state: ClinicianWorkbenchRequestState,
  runtimeConfig: MobileRuntimeConfig = resolveMobileRuntimeConfig(),
): ClinicianWorkbenchRequestV1 {
  return {
    schemaVersion: clinicianWorkbenchContractVersion,
    tenantId: runtimeConfig.requestTenantId,
    siteId: runtimeConfig.requestSiteId,
    clinicianUserId: runtimeConfig.requestClinicianUserId,
    listScope: state.selectedPatientTabId,
    attentionOnly: state.requiresAttentionOnly,
    searchQuery: state.searchQuery,
    selectedPatientId: state.selectedPatientId,
    detailTab: resolveClinicianWorkbenchDetailTab(state.selectedDetailTabId),
  };
}

export async function loadClinicianWorkbenchClientResult(
  state: ClinicianWorkbenchRequestState,
  transport: ClinicianWorkbenchTransport,
  runtimeConfig: MobileRuntimeConfig = resolveMobileRuntimeConfig(),
  signal?: AbortSignal,
): Promise<ClinicianWorkbenchClientResult> {
  const request = buildClinicianWorkbenchShellRequest(state, runtimeConfig);
  const query = buildClinicianWorkbenchHttpQuery(request);

  return {
    request,
    query,
    response: await transport.readClinicianWorkbench({
      query,
      signal,
    }),
  };
}

export function buildClinicianWorkbenchShellActionRequest(
  state: ClinicianWorkbenchRequestState,
  action: ClinicianWorkbenchActionInput,
  runtimeConfig: MobileRuntimeConfig = resolveMobileRuntimeConfig(),
): ClinicianWorkbenchActionRequestV1 {
  return buildClinicianWorkbenchActionRequest(
    buildClinicianWorkbenchShellRequest(state, runtimeConfig),
    action,
  );
}

export async function submitClinicianWorkbenchActionClientResult(
  state: ClinicianWorkbenchRequestState,
  action: ClinicianWorkbenchActionInput,
  transport: ClinicianWorkbenchTransport,
  runtimeConfig: MobileRuntimeConfig = resolveMobileRuntimeConfig(),
  signal?: AbortSignal,
): Promise<ClinicianWorkbenchActionClientResult> {
  const request = buildClinicianWorkbenchShellActionRequest(
    state,
    action,
    runtimeConfig,
  );

  return {
    request,
    response: await transport.submitClinicianWorkbenchAction({
      request,
      signal,
    }),
  };
}

export function buildClinicianWorkbenchOutboundCallsShellRequest(
  state: ClinicianWorkbenchRequestState,
  patientId: string,
  runtimeConfig: MobileRuntimeConfig = resolveMobileRuntimeConfig(),
): ClinicianWorkbenchOutboundCallsRequestV1 {
  const workbenchRequest = buildClinicianWorkbenchShellRequest(
    state,
    runtimeConfig,
  );

  return {
    schemaVersion: clinicianWorkbenchOutboundCallsContractVersion,
    tenantId: workbenchRequest.tenantId,
    siteId: workbenchRequest.siteId,
    clinicianUserId: workbenchRequest.clinicianUserId,
    patientId,
  };
}

export async function loadClinicianWorkbenchOutboundCallsClientResult(
  state: ClinicianWorkbenchRequestState,
  patientId: string,
  transport: ClinicianWorkbenchTransport,
  runtimeConfig: MobileRuntimeConfig = resolveMobileRuntimeConfig(),
  signal?: AbortSignal,
): Promise<ClinicianWorkbenchOutboundCallsClientResult> {
  const request = buildClinicianWorkbenchOutboundCallsShellRequest(
    state,
    patientId,
    runtimeConfig,
  );

  return {
    request,
    response: await transport.readClinicianWorkbenchOutboundCalls({
      request,
      signal,
    }),
  };
}

export function buildClinicianWorkbenchOutboundCallsCommandShellRequest(
  state: ClinicianWorkbenchRequestState,
  input: Omit<
    ClinicianWorkbenchOutboundCallsCommandRequestV1,
    "clinicianUserId" | "schemaVersion" | "siteId" | "tenantId"
  >,
  runtimeConfig: MobileRuntimeConfig = resolveMobileRuntimeConfig(),
): ClinicianWorkbenchOutboundCallsCommandRequestV1 {
  const workbenchRequest = buildClinicianWorkbenchShellRequest(
    state,
    runtimeConfig,
  );

  return {
    ...input,
    schemaVersion: clinicianWorkbenchOutboundCallsCommandContractVersion,
    tenantId: workbenchRequest.tenantId,
    siteId: workbenchRequest.siteId,
    clinicianUserId: workbenchRequest.clinicianUserId,
  };
}

export async function submitClinicianWorkbenchOutboundCallsCommandClientResult(
  state: ClinicianWorkbenchRequestState,
  input: Omit<
    ClinicianWorkbenchOutboundCallsCommandRequestV1,
    "clinicianUserId" | "schemaVersion" | "siteId" | "tenantId"
  >,
  transport: ClinicianWorkbenchTransport,
  runtimeConfig: MobileRuntimeConfig = resolveMobileRuntimeConfig(),
  signal?: AbortSignal,
): Promise<ClinicianWorkbenchOutboundCallsCommandClientResult> {
  const request = buildClinicianWorkbenchOutboundCallsCommandShellRequest(
    state,
    input,
    runtimeConfig,
  );

  return {
    request,
    response: await transport.submitClinicianWorkbenchOutboundCallsCommand({
      request,
      signal,
    }),
  };
}
